import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { fetchPost, fetchPosts, mediaUrl } from "@/lib/api";
import { RichText } from "@/components/RichText";
import { DI, DProductCard } from "@/components/desktop/desktopUi";
import type { Post, PostSummary, Product } from "@/types";

const TYPES = ["news", "article", "promo"] as const;
const dateFmt = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "uz" ? "uz-UZ" : locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));

function PostCard({ p, big }: { p: PostSummary; big?: boolean }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  return (
    <button className={`db-card ${big ? "db-card--big" : ""}`} onClick={() => navigate({ name: "post", slug: p.slug })}>
      <span className="db-card__img">{p.coverImage ? <img src={mediaUrl(p.coverImage, 800)} alt="" /> : <span className="db-card__ph" />}</span>
      <span className="db-card__body">
        <span className="dk-eyebrow">{t(`posts.tabs.${p.type}`)} · {dateFmt(p.publishedAt, locale)}</span>
        <span className="dk-serif db-card__title">{p.title}</span>
        {p.excerpt && <span className="db-card__excerpt">{p.excerpt}</span>}
        <span className="db-card__more">{t("blog.read")} {DI.arrow}</span>
      </span>
    </button>
  );
}

/** Desktop blog list: page header, type tabs, the newest post large, then a grid. */
export function DesktopPosts() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const type = route.name === "posts" ? route.type : undefined;
  const [posts, setPosts] = useState<PostSummary[] | null>(null);
  useEffect(() => {
    setPosts(null);
    fetchPosts({ type, limit: 40 }).then(setPosts).catch(() => setPosts([]));
  }, [type, locale]);
  const [first, ...rest] = posts ?? [];
  return (
    <div className="db">
      <section className="dk-wrap db-head">
        <div className="dc-crumbs"><button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <span>{t("desktop.blog")}</span></div>
        <h1 className="dk-serif">{t("desktop.blogTitle")}</h1>
        <p>{t("blog.lead")}</p>
        <div className="dh-tabs db-tabs">
          <button data-active={!type} onClick={() => navigate({ name: "posts" })}>{t("blog.all")}</button>
          {TYPES.map((x) => <button key={x} data-active={type === x} onClick={() => navigate({ name: "posts", type: x })}>{t(`posts.tabs.${x}`)}</button>)}
        </div>
      </section>
      <section className="dk-wrap">
        {posts === null ? (
          <div className="dc-empty">{t("common.loading")}</div>
        ) : posts.length === 0 ? (
          <div className="dc-empty">{t("posts.empty")}</div>
        ) : (
          <>
            {first && <PostCard p={first} big />}
            {rest.length > 0 && <div className="db-grid">{rest.map((p) => <PostCard key={p.id} p={p} />)}</div>}
          </>
        )}
      </section>
    </div>
  );
}

/** Desktop article: cover, readable text column, linked products, more articles. */
export function DesktopPost({ slug }: { slug: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [data, setData] = useState<{ post: Post; products: Product[] } | null | undefined>(undefined);
  const [more, setMore] = useState<PostSummary[]>([]);
  useEffect(() => {
    setData(undefined);
    fetchPost(slug).then((d) => setData(d ?? null)).catch(() => setData(null));
    fetchPosts({ limit: 4 }).then((x) => setMore(x.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => setMore([]));
  }, [slug, locale]);
  useEffect(() => {
    if (data?.post) document.title = `${data.post.title} — KMS ROASTING`;
  }, [data]);
  if (data === undefined) return <div className="dk-wrap dc-empty db-loading">{t("common.loading")}</div>;
  if (data === null) {
    return (
      <div className="dk-wrap dp-missing">
        <h1 className="dk-serif">{t("posts.empty")}</h1>
        <button className="dk-btn" onClick={() => navigate({ name: "posts" })}>{t("desktop.allArticles")} {DI.arrow}</button>
      </div>
    );
  }
  const { post, products } = data;
  return (
    <div className="db">
      <article className="db-article">
        <div className="dc-crumbs"><button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <button onClick={() => navigate({ name: "posts" })}>{t("desktop.blog")}</button> › <span>{post.title}</span></div>
        <div className="dk-eyebrow db-article__meta">{post.type !== "page" ? `${t(`posts.tabs.${post.type}`)} · ` : ""}{dateFmt(post.publishedAt, locale)}</div>
        <h1 className="dk-serif">{post.title}</h1>
        {post.excerpt && <p className="db-article__lead">{post.excerpt}</p>}
        {post.coverImage && <img className="db-article__cover" src={mediaUrl(post.coverImage, 800)} alt="" />}
        <div className="db-article__body"><RichText text={post.body} /></div>
      </article>
      {products.length > 0 && (
        <section className="dk-wrap">
          <div className="dh-sec"><h2 className="dk-serif">{t("posts.productsTitle")}</h2></div>
          <div className="dh-best">{products.slice(0, 6).map((p) => <DProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
      {more.length > 0 && (
        <section className="dk-wrap">
          <div className="dh-sec"><h2 className="dk-serif">{t("blog.more")}</h2><button className="dk-more" onClick={() => navigate({ name: "posts" })}>{t("desktop.allArticles")} {DI.arrow}</button></div>
          <div className="db-grid">{more.map((p) => <PostCard key={p.id} p={p} />)}</div>
        </section>
      )}
    </div>
  );
}
